import { usePresence } from "@/hooks/usePresence";
import { Users } from "lucide-react";
import { useEffect } from "react";

/**
 * Live presence avatars for a project room.
 * Optional `phase` prop publishes the viewer's current phase to teammates.
 */
export const PresenceAvatars = ({ projectId, token, phase, className = "" }) => {
  const { roster, count, connected, setPhase } = usePresence(projectId, token);

  useEffect(() => {
    if (typeof phase === 'number') setPhase(phase);
  }, [phase, setPhase]);

  if (!projectId || !token) return null;
  if (count <= 1) {
    return (
      <div className={`flex items-center gap-2 text-xs text-zinc-500 ${className}`} data-testid="presence-solo">
        <span className={`inline-block w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-zinc-600'}`} />
        {connected ? 'You are the only one here' : 'Connecting…'}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="presence-roster">
      <Users className="w-3.5 h-3.5 text-zinc-400" />
      <span className="text-xs text-zinc-400 mr-1">{count} live</span>
      <div className="flex -space-x-2">
        {roster.slice(0, 5).map((u) => {
          const tooltip = u.phase
            ? `${u.username} — Phase ${u.phase}`
            : `${u.username} (${u.email})`;
          return (
            <div key={u.user_id} title={tooltip} className="relative group" data-testid={`presence-avatar-${u.user_id}`}>
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white border-2 border-zinc-900 shadow-sm"
                style={{ background: u.color }}
              >
                {u.initials}
              </div>
              {u.phase && (
                <span
                  className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-zinc-900 border border-zinc-700 text-[9px] font-mono font-bold flex items-center justify-center text-white"
                  data-testid={`presence-phase-${u.user_id}`}
                  style={{ color: u.color }}
                >
                  {u.phase}
                </span>
              )}
            </div>
          );
        })}
        {count > 5 && (
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-zinc-300 bg-zinc-700 border-2 border-zinc-900">
            +{count - 5}
          </div>
        )}
      </div>
    </div>
  );
};
